import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { describeError, useToast } from "./toast";
import { useTranslation } from "react-i18next";
import UploadIcon from "@mui/icons-material/DriveFolderUpload";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useCallback, useEffect, useRef, useState } from "react";
import type { CloneFileSummary } from "@shared/types/foyer";
import { foyerApi } from "../http/foyer";
import { useFormat, type Formatters } from "../i18n/format";
import type { Translate } from "../i18n/translate";

/**
 * TEP cloning, step 1 + 2: a premise's Samsung clone files.
 *
 * The operator picks the folder the TV wrote to the USB stick (Hotel Option ->
 * Cloning -> Clone TV to USB), e.g. `T-RSLFDEUC-0090`, or the stick itself.
 * Only two files leave the browser: `Clone/ProductCloneInfo.txt` (the label
 * the server verifies) and `sClone/CloneEncrypted.sclone` (the clone the TV
 * restores). Step 3, the push, lives on each TV's device dialog.
 */

/** Pick the clone's label, encrypted clone and folder name out of a picked tree. */
export function findCloneFiles(files: File[]): {
  info?: File;
  clone?: File;
  folder?: string;
} {
  const rel = (f: File) =>
    (
      (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name
    ).replace(/\\/g, "/");
  const info = files.find((f) =>
    /(^|\/)Clone\/ProductCloneInfo\.txt$/i.test(rel(f))
  );
  const clone = files.find((f) =>
    /(^|\/)sClone\/CloneEncrypted\.sclone$/i.test(rel(f))
  );
  // The folder is the segment directly above Clone/ (the firmware family).
  const parts = info ? rel(info).split("/") : [];
  const folder = parts.length >= 3 ? parts[parts.length - 3] : undefined;
  return { info, clone, folder };
}

const fmtSize = (t: Translate, fmt: Formatters, n: number) =>
  n >= 1024 * 1024
    ? t("premises:cloneFilesPanel.sizeMb", {
        size: fmt.number(n / 1024 / 1024, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        }),
      })
    : t("premises:cloneFilesPanel.sizeKb", {
        size: fmt.number(Math.round(n / 1024)),
      });

const NO_VALUE = "-";

export default function CloneFilesPanel({ premiseId }: { premiseId: string }) {
  const [clones, setClones] = useState<CloneFileSummary[]>([]);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const { t } = useTranslation(["premises", "common"]);
  const fmt = useFormat();
  const input = useRef<HTMLInputElement | null>(null);

  const refresh = useCallback(async () => {
    try {
      setClones((await foyerApi.listClones(premiseId)).clones ?? []);
    } catch (e) {
      toast.error(describeError(t, "loadCloneFiles", e));
    }
  }, [premiseId, toast, t]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function onPicked(list: FileList | null) {
    const files = Array.from(list ?? []);
    if (input.current) input.current.value = "";
    if (files.length === 0) return;
    const { info, clone, folder } = findCloneFiles(files);
    if (!info || !clone) {
      toast.error(
        !info
          ? t("cloneFilesPanel.missingInfo")
          : t("cloneFilesPanel.missingClone"),
        { durationMs: 12000 }
      );
      return;
    }
    setBusy(true);
    try {
      const saved = await foyerApi.uploadClone(premiseId, {
        info: await info.text(),
        folder: folder ?? "",
        clone,
      });
      toast.success(
        t("cloneFilesPanel.saved", {
          model: saved.model,
          firmware: saved.firmware,
        })
      );
      await refresh();
    } catch (e) {
      const body = (e as { body?: { message?: string } }).body;
      toast.error(
        body?.message
          ? t("common:couldNotBecause", {
              action: t("common:actions.uploadClone"),
              reason: body.message.replace(/[.\s]+$/, ""),
            })
          : describeError(t, "uploadClone", e),
        { durationMs: 12000 }
      );
    } finally {
      setBusy(false);
    }
  }

  async function remove(c: CloneFileSummary) {
    if (
      !window.confirm(
        t("cloneFilesPanel.deleteConfirm", {
          model: c.model,
          firmware: c.firmware,
        })
      )
    )
      return;
    try {
      await foyerApi.deleteClone(premiseId, c.id);
      toast.success(
        t("cloneFilesPanel.deleted", { model: c.model, firmware: c.firmware })
      );
      await refresh();
    } catch (e) {
      toast.error(describeError(t, "deleteModelClone", e, { model: c.model }));
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="clone-files-panel">
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="subtitle1" fontWeight="fontWeightSemibold">
              {t("cloneFilesPanel.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("cloneFilesPanel.intro")}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            disabled={busy}
            onClick={() => input.current?.click()}
            data-testid="clone-upload"
          >
            {busy
              ? t("cloneFilesPanel.verifying")
              : t("cloneFilesPanel.uploadFolder")}
          </Button>
          <input
            ref={input}
            type="file"
            hidden
            multiple
            {...({ webkitdirectory: "", directory: "" } as Record<
              string,
              string
            >)}
            onChange={(e) => void onPicked(e.target.files)}
          />
        </Stack>
        {clones.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("cloneFilesPanel.columns.model")}</TableCell>
                <TableCell>{t("cloneFilesPanel.columns.firmware")}</TableCell>
                <TableCell>{t("cloneFilesPanel.columns.folder")}</TableCell>
                <TableCell>{t("cloneFilesPanel.columns.size")}</TableCell>
                <TableCell>{t("cloneFilesPanel.columns.uploaded")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {clones.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>{c.model}</TableCell>
                  <TableCell sx={{ fontFamily: "fontFamilyMono" }}>
                    {c.firmware}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "fontFamilyMono" }}>
                    {c.sourceFolder ?? NO_VALUE}
                  </TableCell>
                  <TableCell>{fmtSize(t, fmt, c.size)}</TableCell>
                  <TableCell>
                    {fmt.dateTime(c.uploadedAt)}
                    {c.uploadedBy ? ` · ${c.uploadedBy}` : ""}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t("cloneFilesPanel.deleteClone")}>
                      <IconButton size="small" onClick={() => void remove(c)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Stack>
    </Paper>
  );
}
